import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Typography, Collapse, Button, Tooltip } from "antd";
import {
  CheckSquareOutlined,
  ReloadOutlined,
  RightOutlined,
  PictureOutlined,
  FormOutlined,
  FileDoneOutlined
} from "@ant-design/icons";
import styled from "styled-components";

import { WHITE, GRAY_200, GRAY_400, GRAY_500, GRAY_700, PRIMARY_600 } from "../../constant/colors";
import { BetaTagContainer, BetaTagText } from "../../component/AIChat/Styles";
import { CountPill } from "./taskStyles";
import { ScreenshotTasksSection } from "./ScreenshotTasksSection";
import { HitlTasksSection } from "./HitlTasksSection";
import { HITL_TASK_TYPES } from "../../constant/agenticPlatform";

const { Title, Text } = Typography;
const { Panel } = Collapse;

// Collapse panel keys — also the stable identity for each section.
const SECTION = {
  SCREENSHOTS: "screenshots",
  EMR_FILL: "emr_fill",
  CONSENT: "consent"
};

const PageContainer = styled.div`
  width: 98%;
  margin: 0 auto;
  padding: 16px;
  background: ${WHITE};
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  min-height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Subtitle = styled(Text)`
  color: ${GRAY_500};
  font-size: 13px;
  display: block;
  margin-top: -4px;
  margin-bottom: 4px;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  font-weight: 500;
  color: ${GRAY_700};
`;

const SectionIcon = styled.span`
  color: ${GRAY_500};
  font-size: 17px;
  display: inline-flex;
`;

// Small per-section refresh icon at the right of each header — muted by
// default, primary on hover, transparent so it reads as an icon, not a button.
const HeaderRefresh = styled(Button)`
  color: ${GRAY_500};
  &:hover,
  &:focus {
    color: ${PRIMARY_600};
    background: transparent;
  }
`;

// Each panel is a standalone card (the mockup's `.sec`): own border, rounded,
// spaced. `bordered={false}` drops antd's default outer frame + dividers so
// these per-item borders stand alone. Section bodies own their padding (the
// tables run flush to the card edges).
const SectionsCollapse = styled(Collapse)`
  background: transparent;

  .ant-collapse-item {
    background: ${WHITE};
    border: 1px solid ${GRAY_200};
    border-radius: 10px;
    margin-bottom: 12px;
    overflow: hidden;
  }
  /* Each panel is a standalone card. antd's borderless Collapse special-cases
     the LAST item — assuming items are dividers in one shared frame — and
     zeroes both its corner radius (collapse/style/index.css:108) and its
     bottom border (index.css:111). We render separate cards, so "&&"
     out-specifies those rules to restore the rounded corners + bottom border;
     the item's overflow:hidden then clips the expanded content to the box. */
  && .ant-collapse-item:last-child {
    border-bottom: 1px solid ${GRAY_200};
    border-radius: 10px;
  }
  .ant-collapse-header {
    align-items: center !important;
    padding: 14px 16px !important;
  }
  .ant-collapse-content > .ant-collapse-content-box {
    padding: 0;
  }
`;

/**
 * Agentic Platform — Tasks hub. One page for everything a navigator /
 * operator needs to act on, replacing the pipeline board + the standalone
 * EMR Tasks page. Three stacked, collapsible card sections, each with a
 * count chip in its header (visible while collapsed) and its own 5/page
 * pager:
 *
 *   1. **EMR screenshots** — bulk-upload UX: stage a mixed-patient batch,
 *      assign it to pending tasks, submit. (`ScreenshotTasksSection`)
 *   2. **Manual EMR fill** — `emr_fill` HITL blockers; the row / "Fill EMR
 *      data" opens the fill modal. (`HitlTasksSection`)
 *   3. **Consent decisions** — `consent_decide` HITL; the row / "Review"
 *      opens the approve/decline modal. (`HitlTasksSection`)
 *
 * Each section fetches its own data and reports its total up so the
 * collapsed headers stay accurate (panels use `forceRender`). Every header has
 * a small refresh icon that refetches just that section; the toolbar's
 * "Refresh all" bumps every section. Sections also auto-refresh every 1m.
 */
const AgenticPlatformTasks = () => {
  const { t } = useTranslation();

  // Deep-link target from the patient-page AgenticRunsModal: `?agenticRunId=<id>`.
  // Passed to both HITL sections; whichever section's feed contains the run
  // auto-opens its modal once (the other ignores it).
  const location = useLocation();
  const deepLinkRunId = new URLSearchParams(location.search).get("agenticRunId");

  // Per-section totals for the collapsible headers. Stable setters so the
  // sections' count-reporting effects don't loop.
  const [counts, setCounts] = useState({ screenshots: 0, emrFill: 0, consent: 0 });
  const setScreenshotsCount = useCallback((c) => setCounts((s) => ({ ...s, screenshots: c })), []);
  const setEmrFillCount = useCallback((c) => setCounts((s) => ({ ...s, emrFill: c })), []);
  const setConsentCount = useCallback((c) => setCounts((s) => ({ ...s, consent: c })), []);

  // Per-section refresh nonces. Each header's small refresh icon bumps its own
  // section's nonce; the global "Refresh all" bumps all three. Sections refetch
  // when their nonce changes (and also auto-refresh every 1m on their own).
  const [refreshNonces, setRefreshNonces] = useState({ screenshots: 0, emrFill: 0, consent: 0 });
  const refreshSection = useCallback((key) => setRefreshNonces((n) => ({ ...n, [key]: n[key] + 1 })), []);
  const refreshAll = useCallback(
    () =>
      setRefreshNonces((n) => ({
        screenshots: n.screenshots + 1,
        emrFill: n.emrFill + 1,
        consent: n.consent + 1
      })),
    []
  );

  const sectionHeader = (icon, label, count, onRefresh, testId, tooltip) => (
    <PanelHeader data-testid={testId}>
      <SectionIcon>{icon}</SectionIcon>
      <Tooltip title={tooltip}>
        <span>{label}</span>
      </Tooltip>
      <CountPill>{count}</CountPill>
      <HeaderRefresh
        type="text"
        size="small"
        icon={<ReloadOutlined />}
        onClick={(e) => {
          // Don't let the click toggle the panel — it only refreshes this section.
          e.stopPropagation();
          onRefresh();
        }}
        aria-label={t("agentic_tasks.refresh_section")}
        data-testid={`${testId}-refresh`}
      />
    </PanelHeader>
  );

  // eslint-disable-next-line react/prop-types
  const expandIcon = ({ isActive }) => (
    <RightOutlined rotate={isActive ? 90 : 0} style={{ color: GRAY_400, fontSize: 13 }} />
  );

  return (
    <PageContainer data-testid="agentic-tasks-page">
      <HeaderRow>
        <HeaderLeft>
          <CheckSquareOutlined style={{ fontSize: 24, color: PRIMARY_600 }} />
          <Title level={3} style={{ margin: 0 }}>
            {t("agentic_tasks.page_title")}
          </Title>
          <BetaTagContainer>
            <BetaTagText>BETA</BetaTagText>
          </BetaTagContainer>
        </HeaderLeft>
        <Button icon={<ReloadOutlined />} onClick={refreshAll} data-testid="agentic-tasks-refresh-all">
          {t("agentic_tasks.refresh_all")}
        </Button>
      </HeaderRow>
      <Subtitle>{t("agentic_tasks.page_subtitle")}</Subtitle>

      <SectionsCollapse
        bordered={false}
        expandIcon={expandIcon}
        defaultActiveKey={[SECTION.SCREENSHOTS, SECTION.EMR_FILL, SECTION.CONSENT]}
      >
        <Panel
          key={SECTION.SCREENSHOTS}
          forceRender
          header={sectionHeader(
            <PictureOutlined />,
            t("agentic_tasks.section_screenshots"),
            counts.screenshots,
            () => refreshSection("screenshots"),
            "agentic-tasks-header-screenshots",
            t("agentic_tasks.tooltip_screenshots")
          )}
        >
          <ScreenshotTasksSection onCountChange={setScreenshotsCount} refreshNonce={refreshNonces.screenshots} />
        </Panel>

        <Panel
          key={SECTION.EMR_FILL}
          forceRender
          header={sectionHeader(
            <FormOutlined />,
            t("agentic_tasks.section_emr_fill"),
            counts.emrFill,
            () => refreshSection("emrFill"),
            "agentic-tasks-header-emr-fill",
            t("agentic_tasks.tooltip_emr_fill")
          )}
        >
          <HitlTasksSection
            hitlTaskType={HITL_TASK_TYPES.EMR_FILL}
            actionLabel={t("agentic_tasks.action_fill_emr")}
            contextTitle={t("agentic_tasks.col_reason")}
            emptyText={t("agentic_tasks.empty_emr_fill")}
            onCountChange={setEmrFillCount}
            refreshNonce={refreshNonces.emrFill}
            autoOpenRunId={deepLinkRunId}
          />
        </Panel>

        <Panel
          key={SECTION.CONSENT}
          forceRender
          header={sectionHeader(
            <FileDoneOutlined />,
            t("agentic_tasks.section_consent"),
            counts.consent,
            () => refreshSection("consent"),
            "agentic-tasks-header-consent",
            t("agentic_tasks.tooltip_consent")
          )}
        >
          <HitlTasksSection
            hitlTaskType={HITL_TASK_TYPES.CONSENT_DECIDE}
            actionLabel={t("agentic_tasks.action_review")}
            contextTitle={t("agentic_tasks.col_state")}
            emptyText={t("agentic_tasks.empty_consent")}
            onCountChange={setConsentCount}
            refreshNonce={refreshNonces.consent}
            autoOpenRunId={deepLinkRunId}
          />
        </Panel>
      </SectionsCollapse>
    </PageContainer>
  );
};

export { AgenticPlatformTasks };
